import { AxiosError } from 'axios';
import { useAccountState } from 'contexts/account/account-context';
import React, { useContext, useMemo } from 'react';
import { UseQueryResult } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { useGet } from 'hooks/use-get';
import { Access, SiteSettings } from 'views/site-settings/types/site-settings-type';
import { getFeatures, getSiteSettings } from '../api/site-settings';
import { SiteSettingsFeatures } from '../types/site-settings-features.type';
import { hasSeaCloudAccess } from '../utils/has-feature';

type State = {
  siteSettingsQuery: UseQueryResult<SiteSettings, AxiosError<unknown, any>>;
  featuresQuery: UseQueryResult<SiteSettingsFeatures, AxiosError<unknown, any>>;
  access: Access;
};

type ParamTypes = {
  globalSiteId: string;
  siteTab: string;
};

const SiteSettingsContext = React.createContext<State | undefined>(undefined);

type SiteSettingsProviderProps = {
  children: React.ReactNode;
};

const SiteSettingsProvider: React.FC<SiteSettingsProviderProps> = ({ children }) => {
  const params = useParams<ParamTypes>();
  const { account } = useAccountState();
  const siteSettingsQuery = useGet(
    ['getSiteSettings', params.globalSiteId],
    (context) => getSiteSettings(String(context.queryKey[1])),
    {
      enabled: !!params.globalSiteId,
      staleTime: 10000,
    }
  );

  const featuresQuery = useGet(['getFeatures', params.globalSiteId], (context) => getFeatures(String(context.queryKey[1])), {
    enabled: !!params.globalSiteId,
  });

  // checks what features are available within the context of a user and the chosen site
  const featuresStatus: { [code: string]: boolean } = useMemo(() => {
    const features = featuresQuery.data;

    return (
      features?.featuresStatus
        .flatMap((ft) => ft.requirementsStatus)
        .reduce((acc, curr) => ({ ...acc, [curr.code]: curr.isValid }), {}) || {}
    );
  }, [featuresQuery.data]);

  const access: Access = useMemo(
    () => ({
      hasProductAccess: !!account.company?.features.hasOrderFeed,
      hasSiloBufferAccess: !!account.company?.features.hasOrderFreeDelivery,
      isMappedToSeaCloud: hasSeaCloudAccess(featuresStatus),
      hasTemperatureAccess: !!(
        (account.company?.features.hasScenarioBuilder || account.company?.features.hasPlanningPrognosis) &&
        hasSeaCloudAccess(featuresStatus)
      ),
    }),
    [featuresStatus, account.company]
  );

  return <SiteSettingsContext.Provider value={{ siteSettingsQuery, featuresQuery, access }}>{children}</SiteSettingsContext.Provider>;
};

function useSiteSettingsState() {
  const context = useContext(SiteSettingsContext);
  if (context === undefined) {
    throw new Error('useSiteSettingsState must be used within a SiteSettingsProvider');
  }
  return context;
}

export { SiteSettingsProvider, useSiteSettingsState };
